/**
 * CircleMembers -- Full member list for a Circle.
 * Route: /circles/:circleSlug/members
 *
 * Auth model:
 *  - Auth required (Cloud Function requires auth)
 *  - Shows all active members with avatar, name, handle, role, join date
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Users, Shield, ChevronLeft, UserPlus } from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { useCircleId } from '../hooks/useCircleId';
import SEO from '../components/SEO';
import GlassPanel from '../components/GlassPanel';
import MonoAvatar from '../components/MonoAvatar';
import CircleInviteSheet from '../components/CircleInviteSheet';
import { getCircleDetail, getCircleMembers } from '../services/circleService';
import type { Circle, CircleMember } from '../types/circles';
import { formatTimestamp } from '../utils/dateUtils';
import '../styles/modern.css';

export default function CircleMembers() {
  const { circleSlug } = useParams<{ circleSlug: string }>();
  const { circleId, resolving } = useCircleId(circleSlug);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();

  const [circle, setCircle] = useState<Circle | null>(null);
  const [members, setMembers] = useState<CircleMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // PPL-215 — the viewer's own membership drives the admin-only invite action.
  const [membership, setMembership] = useState<CircleMember | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  // ─── Auth gate ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      redirectToLogin();
    }
  }, [isAuthenticated, loading, redirectToLogin]);

  // ─── Load data ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!circleId || !isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const [detail, membersRes] = await Promise.all([
        getCircleDetail(circleId),
        getCircleMembers(circleId),
      ]);
      setCircle(detail.circle);
      setMembership(detail.membership);
      setMembers(membersRes.members || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [circleId, isAuthenticated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell">
      <div className="page-shell__inner" style={{ maxWidth: 780 }}>
        {circle && (
          <SEO
            title={`Members - ${circle.name} | pplkpr`}
            description={`Members of the ${circle.name} Circle on pplkpr`}
            url={`https://pplkpr.ai/circles/${circleSlug}/members`}
          />
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button
            onClick={() => navigate(`/circles/${circleSlug}`, { state: { circleId } })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 999, padding: '7px 12px', color: '#fff', fontSize: 13,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <ChevronLeft size={14} />
          </button>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.025em' }}>
              members
            </h2>
            {circle && <span style={{ fontSize: 12, color: 'rgba(220,200,240,0.5)' }}>{circle.name}</span>}
          </div>
          {/* PPL-215 — admin-only invite entry point (server re-checks the role). */}
          {membership?.role === 'admin' && membership?.status === 'active' && (
            <button
              onClick={() => setInviteOpen(true)}
              style={{
                marginLeft: 'auto',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(217,70,239,0.12)', border: '1px solid rgba(217,70,239,0.35)',
                borderRadius: 999, padding: '8px 14px', color: '#f0abfc', fontSize: 13,
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <UserPlus size={13} /> invite
            </button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map((i) => (
              <GlassPanel key={i} hover={false}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
                  <div style={{ flex: 1 }}>
                    <div className="skel-line wide pulse" style={{ height: 14, marginBottom: 6 }} />
                    <div className="skel-line narrow pulse" style={{ height: 10 }} />
                  </div>
                </div>
              </GlassPanel>
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(220,200,240,0.7)' }}>
            <p>{error}</p>
          </div>
        )}

        {/* Members list */}
        {!loading && !error && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {members.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '4rem 2rem',
                color: 'rgba(220,200,240,0.4)',
              }}>
                <Users size={32} style={{ marginBottom: 10 }} />
                <p>no members found</p>
              </div>
            )}

            {members.map((member) => (
              <GlassPanel
                key={member.userId}
                onClick={() => navigate(`/user/${member.userId}`)}
                style={{ padding: '12px 16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <MonoAvatar
                    name={member.firstName || member.tetherHandle || 'M'}
                    size={40}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                        {[member.firstName, member.lastName].filter(Boolean).join(' ') || 'Member'}
                      </span>
                      {member.role === 'admin' && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          fontSize: 10, padding: '2px 7px', borderRadius: 4,
                          background: 'rgba(139,92,246,0.22)', color: '#c9b3e6', fontWeight: 600,
                        }}>
                          <Shield size={10} /> admin
                        </span>
                      )}
                    </div>
                    {member.tetherHandle && (
                      <span style={{ fontSize: 12, color: 'rgba(220,200,240,0.5)' }}>
                        @{member.tetherHandle.replace(/^@/, '')}
                      </span>
                    )}
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    {member.joinedAt && (
                      <span style={{ fontSize: 11, color: 'rgba(220,200,240,0.4)' }}>
                        joined {formatTimestamp(member.joinedAt)}
                      </span>
                    )}
                  </div>
                </div>
              </GlassPanel>
            ))}
          </div>
        )}

        {/* PPL-215 — invite sheet (admins only) */}
        {inviteOpen && circleId && circle && membership?.role === 'admin' && membership?.status === 'active' && (
          <CircleInviteSheet
            circleId={circleId}
            circleName={circle.name}
            uid={user?.uid ?? null}
            // Refresh on CLOSE, not on send: loadData flips `loading` back on
            // and would blank the list behind (and, if nested, unmount) the sheet.
            onClose={() => { setInviteOpen(false); void loadData(); }}
          />
        )}
      </div>
    </div>
  );
}
